/**
 * The canon's `.grip`, unchanged in geometry — six 3px dots in a 9px column, `--text-3`, 3px gap.
 *
 * The button adds NO padding and NO border, because the session card's interior is already at its
 * measured budget: v3's `.sess` is 733px and the built card 739.8 with `grow` collapsed to its 8px
 * minimum, so a padded grip would push the load module further down on every day of the week.
 * The hit area is extended by a pseudo-element instead, which costs no layout.
 *
 * ── Target size, stated rather than left implicit ────────────────────────────────────────────
 * The extended target is **24×27**, which clears WCAG 2.2 SC 2.5.8's 24×24 (AA). It was 15×27, and
 * the shortfall was carried by the criterion's EQUIVALENT exception alone — every act this grip
 * performs is also on the card's ⋯ menu, whose entries are full-width rows. That exception is real
 * and still holds (view ADR §12 requires the menu path regardless, "for switch access and for
 * anyone who cannot hold a pointer steady"), but it is no longer what keeps this conformant: the
 * inline inset is asymmetric, taking the host header's own empty 10px padding on the left and
 * stopping 2px short of the control on the right. See the tokens for the arithmetic.
 */

.grip {
  position: relative;
  display: grid;
  flex: none;
  grid-template-columns: 3px 3px;

  /* 2.5px, which is the 07.09 programator mock's own `gap: '2.5px'`. It was 3px here until
   * 8 September 2026; the two dot columns then measured 9px against the mock's 8.5px. */
  gap: var(--space-2-5px);
  justify-content: center;
  width: 8.5px;
  padding: var(--space-0);
  border: none;
  background: transparent;
  cursor: grab;
}

/* The hit area, without the layout. Asymmetric on the inline axis: the host header's 10px padding
 * to the left is free, the control to the right is 7px away. 10 + 9 + 5 = 24px — see the tokens. */
.grip::after {
  position: absolute;
  inset: var(--grip-hit-inset);
  content: "";
}

.grip:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* A picture, not a control: it keeps the canon's geometry and drops the enlarged hit area, which on
 * a span would be a 15×27 dead zone swallowing presses meant for the row button beside it. */
.decorative {
  cursor: grab;
}

.decorative::after {
  content: none;
}

.dots {
  display: contents;
}

/* `--grip-ink` with a `--text-3` fallback rather than a bare `--text-3`.
 *
 * The dots are decorative — the button's own name says what it moves — so tertiary ink is right for
 * them at rest. What a HOST sometimes needs is to lift them with the rest of a lit surface: the plan
 * builder's drop target draws the whole column in `--text`, grips included, and the alternative was
 * for `DayColumn.module.css` to reach a hashed class name inside this module, which CSS Modules
 * cannot do and should not. The property is set by the host and read here; the fallback is what
 * every host that says nothing gets. Same shape as `shared/ui/scrollbar`'s `--sb-radius`. */
.dots i {
  display: block;
  width: 3px;
  height: 3px;
  background: var(--grip-ink, var(--text-3));
  border-radius: var(--r-circle);
}

/* ── The WIDE cut, KANON `raporty-2026-09-17` ────────────────────────────────────────────────
 * The survey editor's question rows draw this grip as a 16px six-dot glyph standing in a 22px
 * column — `<circle r="1.4">` at x 9/15 and y 6/12/18 of a 24-unit box, which is a 2px dot on a
 * 4px pitch once the glyph is scaled. The plan builder's 8.5px column with 3px dots on a 5.5px
 * pitch is its own canon and is untouched; only a caller passing `wide` gets this. */
.wide {
  grid-template-columns: 2px 2px;
  gap: var(--space-2px);
  width: var(--sz-report-grip);
}

.wide .dots i {
  width: 2px;
  height: 2px;
}

.grip[aria-disabled="true"] {
  cursor: not-allowed;
}

/* `--border-3` rather than `--border-2`: this is the ONLY visual channel saying the grip is closed,
 * and at 1.31:1 the old value was a change from "grey dots" to "slightly greyer dots" that nobody
 * could be expected to read. The dots are decorative, so no text rule applies — but a state carried
 * by nothing else has to be seen. */
.grip[aria-disabled="true"] .dots i {
  background: var(--border-3);
}

/* The refusal, present to assistive technology and not drawn — see the component. */
.reason {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Lifted by keyboard: the dots take the accent, which is the only signal a keyboard user has that
 * the arrows now move a card rather than the focus. Within the scalpel budget — it is a live state
 * during one gesture, and there is exactly one lifted item on the screen at a time. */
.grip[data-lifted] .dots i {
  background: var(--accent);
}
